import { query } from '@trlogic/client-js';
import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import MessageUtil from '../util/MessageUtil';
import LanguageUtil from '../util/LanguageUtil';
import DeployErrorUtil from '../util/DeployErrorUtil';
import React from 'react';
import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import FolderClient from '../client/folder/FolderClient';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import { ActionType } from '../constant/Constants';
import Tracker from '../domain/tracker/Tracker';
import TrackerClient from '../client/tracker/TrackerClient';
import TransactionClient from '../client/transaction/TransactionClient';
import ComponentName from '../constant/ComponentName';
import { changeTrackerDirty } from '../redux/actions/TrackerActions';
import Query = query.Query;
import { BulkActionDependencyError } from 'redux/reducers/BulkActionReducer';

class TrackerService {
  private trackerClient: TrackerClient = GatewayContainer.getInstance().get(GatewayNames.TRACKER_CLIENT);
  private folderClient: FolderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);
  private transactionClient: TransactionClient = GatewayContainer.getInstance().get(GatewayNames.TRANSACTION_CLIENT);

  public saveTracker = async (tracker: Tracker, isUpdate: boolean, folder: EnrichedComponentCategory, messageShow: boolean = true) => {
    try {
      if (tracker == undefined) return false;
      if (tracker.name == undefined || tracker.name == '') {
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'messages.error.withoutName', defaultMessage: 'You can not save without name' }),
        });
        return false;
      }
      if (!isUpdate) {
        const isExist = await this.trackerClient.isExist(tracker.name);
        if (isExist) {
          MessageUtil.alreadyExist(LanguageUtil.get({ id: 'common.label.tracker' }));
          return false;
        }
      }
      await this.setEventVariableMappingsWithChannel(tracker);
      await this.trackerClient.save(tracker);

      if (!isUpdate && folder) {
        await this.folderClient.addItemToCategory(ComponentName.TRACKER, folder.id, tracker.name);
      }
      if (messageShow) {
        MessageUtil.success({
          summary: LanguageUtil.get({ id: 'common.label.tracker' }),
          detail: (
            <div data-testid='channel_save_message'>
              <b>{tracker.name}</b> {LanguageUtil.get({ id: 'messages.save', defaultMessage: 'saved.' })}
            </div>
          ),
        });
      }
      dispatch(changeTrackerDirty(false));
      return true;
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.tracker' }),
        detail: (
          <div>
            <b>{tracker.name}</b> {LanguageUtil.get({ id: 'messages.cannotSaved', defaultMessage: 'not saved' })}
          </div>
        ),
      });
      return false;
    }
  };

  public removeTracker = async (tracker: Tracker): Promise<boolean> => {
    try {
      const response: any = await this.trackerClient.remove(tracker.name);
      if (response.code) {
        return false;
      }
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.tracker' }),
        detail: (
          <div>
            <b>{tracker.name}</b> {LanguageUtil.get({ id: 'messages.removed', defaultMessage: 'removed' })}
          </div>
        ),
      });
      return true;
    } catch (e: any) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.tracker', defaultMessage: 'Tracker' }))) {
        return false;
      }
      const dependencies: any = e.response.data;
      if (dependencies?.message == 'Dependency Exception') {
        dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.TRACKER));
      }
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.tracker' }),
        detail: (
          <div>
            <b>{tracker.name}</b>
            {LanguageUtil.get({ id: 'messages.cannotRemoved', defaultMessage: 'can not remove' })}
          </div>
        ),
      });
      return false;
    }
  };

  public deployTracker = async (tracker: Tracker): Promise<void> => {
    if (tracker && tracker.name && tracker.name.trim().length > 0) {
      try {
        await this.trackerClient.deploy(tracker.name);
        MessageUtil.success({
          summary: LanguageUtil.get({ id: 'common.label.tracker' }),
          detail: (
            <div>
              <b>{tracker.name}</b> {LanguageUtil.get({ id: 'messages.deploy', defaultMessage: 'deployed' })}
            </div>
          ),
        });
      } catch (error: unknown) {
        if (DeployErrorUtil.show(error, LanguageUtil.get({ id: 'common.label.tracker', defaultMessage: 'Tracker' }))) {
          return;
        }
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'common.label.tracker' }),
          detail: (
            <div>
              <b>{tracker.name}</b> {LanguageUtil.get({ id: 'messages.cannotdeployed', defaultMessage: 'can not deployed' })}
            </div>
          ),
        });
      }
    } else {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.withoutName', defaultMessage: 'You can not save without name' }),
      });
    }
  };

  public unDeployTracker = async (tracker: Tracker): Promise<void> => {
    if (tracker.name) {
      try {
        await this.trackerClient.undeploy(tracker.name);
        MessageUtil.success({
          summary: LanguageUtil.get({ id: 'common.label.tracker' }),
          detail: (
            <div>
              <b>{tracker.name}</b> {LanguageUtil.get({ id: 'messages.undeployed', defaultMessage: 'undeployed' })}
            </div>
          ),
        });
      } catch (e: any) {
        if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.tracker', defaultMessage: 'Tracker' }))) {
          return;
        }
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'common.label.tracker' }),
          detail: (
            <div>
              <b>{tracker.name}</b> {LanguageUtil.get({ id: 'messages.cannotUndeployed', defaultMessage: 'can not undeployed' })}
            </div>
          ),
        });
      }
    }
  };

  public async bulkAction(ids: string[], type: ActionType): Promise<BulkActionDependencyError[]> {
    const logs: any = await this.trackerClient.bulkAction(ids, type);
    return logs;
  }

  public getTrackerCount(query: Query): Promise<number> {
    return this.trackerClient.count(query);
  }

  private async setEventVariableMappingsWithChannel(tracker: Tracker): Promise<void> {
    if (!tracker?.event) return;
    const { name } = tracker.event;
    tracker.event.variableMappings = tracker.event.variableMappings || {};

    if (!name) {
      tracker.event.variableMappings = {};
      return;
    }

    try {
      const transaction = await this.transactionClient.getByName(name);
      const variables = transaction?.variables ?? [];

      if (!variables.length) {
        tracker.event.variableMappings = {};
        return;
      }

      const names = new Set(variables.map((variable) => variable.name));
      Object.keys(tracker.event.variableMappings).forEach((key) => {
        if (!names.has(key)) {
          delete tracker.event.variableMappings[key];
        }
      });
    } catch (error) {
      console.error('TrackerService.setEventVariableMappings', error);
    }
  }
}

export default TrackerService;
